import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import React, { RefObject, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import store, { AppDispatch, RootState } from '../../../stores/store';
import {
  setInspector,
  updateInspectionTop,
} from '../../../stores/inspectionTop/slice';
import ConfirmModal from '../../../_common/Modal/ConfirmModal';
import { useTranslation } from 'react-i18next';
import {
  convertQcTypeDataToInspectionTypeData,
  isNa,
  getStatusInspectionOfType,
  isComplete,
  calcCategoryProgress,
  getStringBtnFinishTest,
} from '../../../_utils/inspectionUtils';
import { fetchJudgementData } from '../../../stores/judgement/slice';
import {
  HISTORY_STATUSES,
  HISTORY_TYPE,
  INSPECTION_STATUSES,
  JUDGEMENT,
  TEMPLATE_CLASS,
  VARIABLE_HARDNESS,
} from '../../../_constants/inspectionConst';
import { isInspection, isStringInput } from '../../../_utils/utils';
import {
  fetchInspectionTypeData,
  fetchQcTypeData,
  fetchQcTypeDataAll,
  updateInspectionTypeData,
  updateIsAllOk,
  updateJudgement,
} from '../../../stores/inspectionTypeData/slice';
import {
  GetInspectionItem,
  GetInspectionType,
  InspectionType,
} from '../../../services/apiTypes';
import useResetStore from '../../../hooks/useResetStore';
import useErrorModal from '../../../hooks/useErrorModal';
import ErrorModal from '../../../_common/Modal/ErrorModal';
import { INSPEX_PATHNAME } from '../../../_constants/Const';
import { isFulfilled, isRejected } from '@reduxjs/toolkit';
import {
  isGetInspectionType,
  isGetQcType,
  isGetQcTypeList,
  isJudgementData,
} from '../../../_utils/typeGuardUtils';
import { MaxLength } from '../../../_constants/maxLengthConst';
import { API_CODE } from '../../../_constants/apiConst';
import useUpdateStatus from '../../../hooks/useUpdateStatus';
import useSaveHistory from '../../../hooks/useSaveHistory';
import { LOADING_BACKDROP_STYLE } from '../../../_constants/styleConst';

type CategoryTabsProps = {
  updateInputStatus: () => 0 | 1 | 2;
  changeType: (newCategoryId: string, newTypeId: string) => void;
  targetsToUpdateTypeSet: RefObject<Set<string>>;
};

/** 大項目タブ */
const CategoryTabs: React.FC<CategoryTabsProps> = ({
  updateInputStatus,
  changeType,
  targetsToUpdateTypeSet,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { resetInspectionStore } = useResetStore();
  const { error, showApiError, showTypeError, showClientError, hideError } =
    useErrorModal();
  const updateStatus = useUpdateStatus();
  const saveHistory = useSaveHistory();

  const { categoryId, typeId } = useSelector(
    (state: RootState) => state.selectIds,
  );
  const { inspectionTop } = useSelector(
    (state: RootState) => state.inspectionTop,
  );
  const { typeLabelsMap } = useSelector((state: RootState) => state.typeLabels);
  const { inspectionTypeDataMap, before, isAllOk } = useSelector(
    (state: RootState) => state.inspectionTypeData,
  );
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );

  /** 中断の確認ダイアログ */
  const [isOpenSaveProgress, setIsOpenSaveProgress] = useState(false);
  /** 検査完了の確認ダイアログ */
  const [isOpenFinishTest, setIsOpenFinishTest] = useState(false);
  /** 返品修理の確認ダイアログ */
  const [isOpenReturnRepair, setIsOpenReturnRepair] = useState(false);
  /** 通常診断に変更の確認ダイアログ */
  const [isOpenChangeInspection, setIsOpenChangeInspection] = useState(false);
  /** 通常診断に変更のボタンを表示するか */
  const [isDisplayChangeButton, setIsDisplayChangeButton] = useState(false);

  const [countNotNaOnFirstLoad, setCountNotNaOnFirstLoad] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  /** 検査者名 */
  const [newInspector, setNewInspector] = useState(inspectionTop.inspectorName);

  const currentTypeDataLength =
    inspectionTypeDataMap[categoryId]?.[typeId]?.itemData.length;

  const style = {
    categoryTab: {
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.caption.fontWeight,
      height: '2rem',
      alignContent: 'center',
    },
    categoryName: {
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
      textOverflow: 'ellipsis',
      wordBreak: 'keep-all',
      overflowWrap: 'word-break',
      whiteSpace: 'pre-wrap',
    },
    button: {
      fontSize: theme.typography.button.fontSize,
      marginTop: '1rem',
      width: '85%',
      height: '2.5rem',
      lineHeight: '1.2',
    },
  };

  useEffect(() => {
    const currentTemplate = inspectionTop.inspectionTypeList.find(
      (temp) => temp.id === inspectionTop.selectedInspectionTypeId,
    );
    if (!currentTemplate || !repairRequest) {
      return;
    }
    setIsDisplayChangeButton(
      isInspection(repairRequest.status) &&
        currentTemplate.templateClass === TEMPLATE_CLASS.SIMPLE,
    );
  }, [
    repairRequest,
    inspectionTop.selectedInspectionTypeId,
    inspectionTop.inspectionTypeList,
  ]);

  useEffect(() => {
    const typeData = inspectionTypeDataMap[categoryId]?.[typeId]?.itemData;
    if (!typeData) return;
    setCountNotNaOnFirstLoad(
      typeData.filter((item) => !isNa(item.judgement)).length,
    );
  }, [categoryId, typeId, currentTypeDataLength]);

  useEffect(() => {
    (async () => {
      if (
        !repairRequest ||
        isInspection(repairRequest.status) ||
        (Object.keys(inspectionTypeDataMap).length !== 0 &&
          Object.keys(before).length !== 0)
      ) {
        return;
      }
      const response = await dispatch(
        fetchQcTypeDataAll({ rmaNo: repairRequest.rmaNo }),
      );
      if (isFulfilled(response)) {
        if (!isGetQcTypeList(response.payload)) {
          showTypeError(API_CODE.QC_ALL);
        }
      } else {
        showApiError(response.payload, API_CODE.QC_ALL, () =>
          navigate(INSPEX_PATHNAME.INSPECTION_TOP),
        );
      }
    })();
  }, []);

  useEffect(() => {
    if (
      repairRequest &&
      !isInspection(repairRequest.status) &&
      !(
        Object.keys(inspectionTypeDataMap).length === 0 ||
        Object.keys(before).length === 0
      )
    ) {
      if (isAllComplete) {
        dispatch(updateIsAllOk());
      }
    }
  }, [inspectionTypeDataMap, before, inspectionTop.inspectionCategoryList]);

  /** 大項目のタブ選択 */
  const onClickTab = (categoryId: string) => {
    if (typeLabelsMap[categoryId]) {
      const newTypeId = typeLabelsMap[categoryId][0].id;
      changeType(categoryId, newTypeId);
    }
  };

  /** 検査者名変更 */
  const handleInspectorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewInspector(e.target.value);
  };

  /** 検査者名を保存 */
  const handleInspectorBlur = () => {
    // 過剰レンダリング防止のため、フォーカスアウトしてからdispatch
    dispatch(setInspector(newInspector));
  };

  /** judgementの更新APIの呼び出し&storeのjudgementを更新 */
  const updateQcTypeData = async (
    newInspectionType: InspectionType,
  ): Promise<{ isSuccess: boolean; error: unknown }> => {
    const response = await dispatch(
      updateInspectionTypeData({
        newInspectionType: newInspectionType,
        isInspection: false,
      }),
    );

    if (isRejected(response)) {
      return { isSuccess: false, error: response.payload };
    }

    newInspectionType.itemData.forEach((item) => {
      dispatch(
        updateJudgement({
          categoryId: newInspectionType.categoryId,
          typeId: newInspectionType.typeId,
          path: [item.id],
          judgement: item.judgement,
        }),
      );
      if (item.itemData.length > 0) {
        item.itemData.forEach((problem) => {
          dispatch(
            updateJudgement({
              categoryId: newInspectionType.categoryId,
              typeId: newInspectionType.typeId,
              path: [item.id, problem.id],
              judgement: problem.judgement,
            }),
          );

          if (problem.itemData.length > 0) {
            problem.itemData.forEach((problemSub) => {
              dispatch(
                updateJudgement({
                  categoryId: newInspectionType.categoryId,
                  typeId: newInspectionType.typeId,
                  path: [item.id, problem.id, problemSub.id],
                  judgement: problemSub.judgement,
                }),
              );
            });
          }
        });
      }
    });
    return { isSuccess: true, error: '' };
  };

  /** 更新対象の中項目を保存 */
  const updateTargetType = async () => {
    let promiseAllResult: {
      isSuccess: boolean;
      error: unknown;
    } = { isSuccess: true, error: '' };
    if (!repairRequest) {
      return false;
    }
    // 現在の中項目は先に保存しているので除外
    const target = Array.from(targetsToUpdateTypeSet.current).filter(
      (targetTypeId) => targetTypeId !== typeId,
    );
    await Promise.all(
      target.map(async (typeId) => {
        // 直前の連動処理でstoreが更新されている可能性があるため、最新の値を参照する
        const latestInspectionTypeData =
          store.getState().inspectionTypeData.inspectionTypeDataMap;
        const [targetCategoryId, _] = typeId.split('-');
        const param = {
          newInspectionType: latestInspectionTypeData[targetCategoryId][typeId],
          isInspection: isInspection(repairRequest.status),
        };
        const response = await dispatch(updateInspectionTypeData(param));
        if (isRejected(response)) {
          promiseAllResult = { isSuccess: false, error: response.payload };
        }
      }),
    );
    // 1件以上更新に失敗した場合
    if (!promiseAllResult.isSuccess) {
      const apiCode = isInspection(repairRequest.status)
        ? API_CODE.INSPECTION_TYPE
        : API_CODE.QC_TYPE;
      showApiError(promiseAllResult.error, apiCode);
      return false;
    }
    return true;
  };

  /**
   * ボタン共通処理（ボタン押下直後）
   * 検査者名入力チェックと入力ステータス更新
   */
  const handleCommonProcess = () => {
    // 検査者名入力チェック
    if (!isStringInput(inspectionTop.inspectorName)) {
      showClientError(t('ERR_INPUT_EMPTY', [t('LBL_INSPECTOR_NAME')]));
      return false;
    }
    updateInputStatus();
    return true;
  };

  /**
   * ボタン共通処理
   * 中項目と進捗の更新
   */
  const updateInspectionData = async (isInspection: boolean) => {
    // 現在の中項目を保存
    const param = {
      newInspectionType: inspectionTypeDataMap[categoryId][typeId],
      isInspection,
    };
    const response = await dispatch(updateInspectionTypeData(param));
    if (isRejected(response)) {
      const apiCode = isInspection
        ? API_CODE.INSPECTION_TYPE
        : API_CODE.QC_TYPE;
      showApiError(response.payload, apiCode);
      return false;
    }

    // 更新対象の中項目を保存
    const isAllSuccess = await updateTargetType();
    if (!isAllSuccess) {
      return false;
    }
    const responseTop = await dispatch(
      updateInspectionTop({
        newInspectionTop: store.getState().inspectionTop.inspectionTop,
        isInspection,
      }),
    );
    if (isRejected(responseTop)) {
      showApiError(responseTop.payload, API_CODE.INSPECTION_TOP);
      return false;
    }
    return true;
  };

  /** 検査完了ボタン押下 */
  const onClickFinishTest = async () => {
    if (!repairRequest) {
      return;
    }
    // 現在の中項目が全てN/AでなければAPIは投げずに確認ダイアログを開く
    const typeData = inspectionTypeDataMap[categoryId][typeId].itemData;
    const isCurrentTypeAllNa = typeData.every((item) => isNa(item.judgement));
    if (!isCurrentTypeAllNa) {
      setIsOpenFinishTest(true);
      return;
    }

    // 全てN/Aかどうかチェック
    const response = await dispatch(
      fetchJudgementData({ rmaNo: repairRequest.rmaNo }),
    );

    if (isFulfilled(response)) {
      if (!isJudgementData(response.payload)) {
        showTypeError(API_CODE.INSPECTION_JUDGEMENTS);
        return;
      }
    } else {
      showApiError(response.payload, API_CODE.INSPECTION_JUDGEMENTS);
      return;
    }

    const { empty, ok, ng } = response.payload;
    if (
      empty + ok + ng === countNotNaOnFirstLoad ||
      (empty === 0 && ok === 0 && ng === 0)
    ) {
      showClientError('ERR_CONFIRM_ALL_NA');
      return;
    }
    setIsOpenFinishTest(true);
  };

  /** 中断の確認ダイアログのOK押下 */
  const executeSaveProgress = async () => {
    setIsOpenSaveProgress(false);
    if (!repairRequest) {
      return;
    }
    // 各処理を実行する前のチェックとステータス更新
    const statusData = handleCommonProcess();
    if (!statusData) {
      return;
    }
    // 中項目と進捗等の更新
    const isInspectionStatus = isInspection(repairRequest.status);
    const isSuccess = await updateInspectionData(isInspectionStatus);
    if (!isSuccess) {
      return;
    }
    /** 操作履歴(受入診断・出荷検査のSave)を保存 */
    if (repairRequest) {
      saveHistory(
        repairRequest,
        isInspectionStatus
          ? HISTORY_STATUSES.INSPECTION.id
          : HISTORY_STATUSES.QC.id,
        HISTORY_TYPE.SAVE.id,
        () => navigate(INSPEX_PATHNAME.INSPECTION_TOP),
        showApiError,
      );
    }
  };

  /** 通常診断に変更の確認ダイアログのOK押下 */
  const onClickOkChangeInspection = async () => {
    setIsOpenChangeInspection(false);
    if (!repairRequest) {
      return;
    }
    // 各処理を実行する前のチェックとステータス更新
    const statusData = handleCommonProcess();
    if (!statusData) {
      return;
    }
    // 中項目と進捗等の更新
    const isSuccess = await updateInspectionData(true);
    if (!isSuccess) {
      return;
    }

    const fullInspectionTemplates = inspectionTop.inspectionTypeList.filter(
      (temp) => temp.templateClass === TEMPLATE_CLASS.FULL,
    );
    // 通常診断のテンプレートが複数ある場合はシリアルチェック画面で選択させる
    if (fullInspectionTemplates.length >= 2) {
      // ステータスをシリアルチェック中に戻す
      const isSuccessStatus = await updateStatus(
        INSPECTION_STATUSES.INSPECTION_SERIAL_CHECK,
        0,
        showApiError,
      );

      if (!isSuccessStatus) {
        return;
      }
    } else {
      // 一つのみの場合はセットして更新する。
      const response = await dispatch(
        updateInspectionTop({
          newInspectionTop: {
            ...inspectionTop,
            selectedInspectionTypeId: fullInspectionTemplates[0].id,
          },
          isInspection: isInspection(repairRequest.status),
        }),
      );
      if (isRejected(response)) {
        showApiError(response.payload, API_CODE.INSPECTION_TOP);
        return;
      }
    }

    /** 操作履歴(受入診断のSave)を保存 */
    saveHistory(
      repairRequest,
      HISTORY_STATUSES.INSPECTION.id,
      HISTORY_TYPE.SAVE.id,
      () => {
        // storeの診断関連のデータを初期化しておく
        resetInspectionStore();
        navigate(
          fullInspectionTemplates.length >= 2
            ? INSPEX_PATHNAME.SERIAL_CHECK
            : INSPEX_PATHNAME.INSPECTION_TOP,
        );
      },
      showApiError,
    );
  };

  /** 返品処理の確認ダイアログのOK押下 */
  const executeReturnRepair = async () => {
    setIsOpenReturnRepair(false);
    if (!repairRequest) {
      return;
    }
    // 各処理を実行する前のチェックとステータス更新
    const isExecutable = handleCommonProcess();
    if (!isExecutable) {
      return;
    }

    // 前回値で上書き
    if (!(await applyPreviousResult())) {
      setIsLoading(false);
      return;
    }

    const isSuccess = await updateStatus(
      INSPECTION_STATUSES.QC_CONFIRM,
      inspectionTop.qcCount,
      showApiError,
    );
    if (!isSuccess) {
      setIsLoading(false);
      return;
    }

    /** 操作履歴(出荷検査のSave)を保存 */
    saveHistory(
      repairRequest,
      HISTORY_STATUSES.QC.id,
      HISTORY_TYPE.SAVE.id,
      () => navigate(INSPEX_PATHNAME.CONFIRMED_QC),
      showApiError,
    );
    setIsLoading(false);
  };

  /** 検査完了の確認ダイアログのOK押下 */
  const executeFinishTest = async () => {
    setIsOpenFinishTest(false);
    if (!repairRequest) {
      return;
    }
    // 各処理を実行する前のチェックとステータス更新
    const statusData = handleCommonProcess();
    if (!statusData) {
      return;
    }
    // 受入診断
    if (isInspection(repairRequest.status)) {
      // 中項目と進捗等の更新
      const isSuccessUpdate = await updateInspectionData(true);
      if (!isSuccessUpdate) {
        return;
      }
      // ステータス更新
      const isSuccess = await updateStatus(
        INSPECTION_STATUSES.INSPECTION_INPUT_RESULT,
        0,
        showApiError,
      );
      if (!isSuccess) {
        return;
      }

      /** 操作履歴(受入診断のCompletion)を保存 */
      saveHistory(
        repairRequest,
        HISTORY_STATUSES.INSPECTION.id,
        HISTORY_TYPE.COMPLETION.id,
        () => navigate(INSPEX_PATHNAME.INSPECTION_INPUT_RESULT),
        showApiError,
      );
      return;
    }
    // 出荷検査
    setIsLoading(true);
    if (!(await updateInspectionQcValueAllOk())) {
      setIsLoading(false);
      return;
    }
    // 進捗率・検査者名・類別を保存
    const responseTop = await dispatch(
      updateInspectionTop({
        newInspectionTop: inspectionTop,
        isInspection: isInspection(repairRequest.status),
      }),
    );
    if (isRejected(responseTop)) {
      showApiError(responseTop.payload, API_CODE.INSPECTION_TOP);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);

    const isSuccessQc = await updateStatus(
      // ExFMから登録され、1度もデータ連携していないか
      repairRequest.isCreatedByExFM && !repairRequest.linkedQcToExFM
        ? INSPECTION_STATUSES.QC_NOT_LINKED
        : INSPECTION_STATUSES.QC_COMPLETE,
      inspectionTop.qcCount ?? 0,
      showApiError,
    );
    if (!isSuccessQc) {
      return;
    }

    /** 操作履歴(出荷検査のCompletion)を保存 */
    saveHistory(
      repairRequest,
      HISTORY_STATUSES.QC.id,
      HISTORY_TYPE.COMPLETION.id,
      () =>
        navigate(INSPEX_PATHNAME.REPORT, {
          state: {
            defaultQcCount: inspectionTop.qcCount,
          },
        }),
      showApiError,
    );
  };

  /** 選択状況と進捗率に応じてタブの色を変更 */
  const applyColorStyle = (id: string, progress: number) => {
    if (id === categoryId) {
      return {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
      };
    }

    if (progress === 100) {
      return {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        '&:hover': {
          backgroundColor: theme.palette.secondary.dark,
        },
      };
    }
    return {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.common.black,
      '&:hover': {
        backgroundColor: theme.palette.grey[400],
      },
    };
  };

  const isAllComplete =
    inspectionTop &&
    inspectionTop.inspectionCategoryList.every(
      (category) => category.progress === 100,
    );

  /** 返品修理時にN/Aと空の項目を前回結果で上書き*/
  const applyPreviousResult = async () => {
    // 最新値を取得
    const after = store.getState().inspectionTypeData.inspectionTypeDataMap;
    if (Object.keys(after).length === 0 || Object.keys(before).length === 0) {
      return false;
    }
    const updateProgress: { [key: string]: number } = {};
    let promiseAllResult: {
      isSuccess: boolean;
      error: unknown;
    } = { isSuccess: true, error: '' };
    await Promise.all(
      inspectionTop.inspectionCategoryList.map(async (category) => {
        let completeNum = 0;
        for (const [typeId, afterTypeData] of Object.entries(
          after[category.id],
        )) {
          // 出荷検査で項目数が増える場合は仮でbeforeにafterの値を渡して項目を追加する
          const beforeTypeData =
            before[category.id] && before[category.id][typeId]
              ? before[category.id][typeId]
              : afterTypeData;
          const updatedData = convertQcTypeDataToInspectionTypeData({
            before: beforeTypeData,
            after: afterTypeData,
          });
          const status = getStatusInspectionOfType(updatedData.itemData);
          completeNum = isComplete(status) ? completeNum + 1 : completeNum;
          const result = await updateQcTypeData(updatedData);
          // 1件でもエラーになれば全体として失敗にする(最後のエラーを表示する)
          if (!result.isSuccess) {
            promiseAllResult = result;
          }
        }
        updateProgress[category.id] = calcCategoryProgress(
          completeNum,
          typeLabelsMap[category.id].length,
        );
      }),
    );

    // 1件以上更新に失敗した場合
    if (!promiseAllResult.isSuccess) {
      showApiError(promiseAllResult.error, API_CODE.QC_TYPE);
      return false;
    }

    // 進捗率を更新する
    const response = await dispatch(
      updateInspectionTop({
        newInspectionTop: {
          ...inspectionTop,
          inspectionCategoryList: inspectionTop.inspectionCategoryList.map(
            (category) => ({
              ...category,
              progress: updateProgress[category.id],
            }),
          ),
        },
        isInspection: false,
      }),
    );

    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.QC_TOP);
      return false;
    }

    return true;
  };

  /** 出荷検査完了時、全てのjudgementをOKに更新 */
  const updateInspectionQcValueAllOk = async () => {
    // 最新値を取得
    const after = store.getState().inspectionTypeData.inspectionTypeDataMap;
    if (Object.keys(after).length === 0 || Object.keys(before).length === 0) {
      return false;
    }
    let promiseAllResult: {
      isSuccess: boolean;
      error: unknown;
    } = { isSuccess: true, error: '' };
    await Promise.all(
      inspectionTop.inspectionCategoryList.map(async (category) => {
        for (const [, afterTypeData] of Object.entries(after[category.id])) {
          const updateData = {
            ...afterTypeData,
            itemData: afterTypeData.itemData.map((item) => {
              if (!item.itemData || item.itemData.length === 0) {
                return { ...item, judgement: JUDGEMENT.OK };
              } else {
                return {
                  ...item,
                  judgement: JUDGEMENT.OK,
                  itemData: item.itemData.map((problem) => {
                    if (!problem.itemData || problem.itemData.length === 0) {
                      return { ...problem, judgement: JUDGEMENT.OK };
                    } else {
                      return {
                        ...problem,
                        judgement: JUDGEMENT.OK,
                        itemData: problem.itemData.map((problemSub) => {
                          return { ...problemSub, judgement: JUDGEMENT.OK };
                        }),
                      };
                    }
                  }),
                };
              }
            }),
          };
          const result = await updateQcTypeData(updateData);
          // 1件でもエラーになれば初回のエラーを表示する
          if (promiseAllResult.isSuccess) {
            promiseAllResult = result;
          }
        }
      }),
    );

    // 1件以上更新に失敗した場合
    if (!promiseAllResult.isSuccess) {
      showApiError(promiseAllResult.error, API_CODE.QC_TYPE);
    }

    return promiseAllResult.isSuccess;
  };

  /** 開発者向け：ALL OKボタンをクリック */
  const onClickAllOk = async () => {
    if (!repairRequest) {
      return;
    }
    inspectionTop.inspectionCategoryList.forEach(({ id: categoryId }) => {
      typeLabelsMap[categoryId].forEach(async ({ id: typeId }) => {
        // 全中項目のデータをGET
        let typeData;
        if (isInspection(repairRequest.status)) {
          const response = await dispatch(
            fetchInspectionTypeData({
              rmaNo: repairRequest.rmaNo,
              categoryId,
              typeId,
            }),
          );
          if (!isGetInspectionType(response.payload)) {
            return;
          }
          typeData = response.payload;
        } else {
          const response = await dispatch(
            fetchQcTypeData({
              rmaNo: repairRequest.rmaNo,
              categoryId,
              typeId,
            }),
          );
          if (!isGetQcType(response.payload)) {
            return;
          }
          typeData = response.payload.after;
        }

        const updatedType: GetInspectionType = {
          ...typeData,
          itemData: [],
        };
        // 取得したデータを深掘りながら結果を上書く
        typeData.itemData.forEach((depth0) => {
          const updatedDepth0: GetInspectionItem = {
            ...depth0,
            judgement: JUDGEMENT.OK,
            itemData: [],
          };
          // 数値はCriteriaの値を入れておく
          if (depth0.inputType === 1) {
            updatedDepth0.numericalValue =
              depth0.criteria.min ?? depth0.criteria.max;
          }
          depth0.itemData.forEach((depth1) => {
            const updatedDepth1: GetInspectionItem = {
              ...depth1,
              judgement: JUDGEMENT.OK,
              itemData: [],
            };
            // 硬度可変測定は階層1の数値を調整
            if (typeId === VARIABLE_HARDNESS.typeId) {
              updatedDepth1.numericalValue =
                depth0.id === '6-7-1'
                  ? 0
                  : (depth0.criteria.min ?? depth0.criteria.max);
            }
            depth1.itemData.forEach((depth2) =>
              updatedDepth1.itemData.push({
                ...depth2,
                judgement: JUDGEMENT.OK,
              }),
            );
            updatedDepth0.itemData.push(updatedDepth1);
          });
          updatedType.itemData.push(updatedDepth0);
        });
        // 中項目ごとにPUT
        dispatch(
          updateInspectionTypeData({
            newInspectionType: { ...updatedType, status: 2 },
            isInspection: isInspection(repairRequest.status),
          }),
        );
      });
      // 進捗率を全て100%で更新
      dispatch(
        updateInspectionTop({
          newInspectionTop: {
            ...inspectionTop,
            inspectionCategoryList: inspectionTop.inspectionCategoryList.map(
              (category) => {
                return { ...category, progress: 100 };
              },
            ),
          },
          isInspection: isInspection(repairRequest.status),
        }),
      );
    });

    // Reduxのstoreは更新していないので、結果入力画面には適切に反映されない
    // そのため、ステータスを更新して画面遷移する
    const isSuccess = await updateStatus(
      isInspection(repairRequest.status)
        ? INSPECTION_STATUSES.INSPECTION_INPUT_RESULT
        : repairRequest.isCreatedByExFM
          ? INSPECTION_STATUSES.QC_NOT_LINKED
          : INSPECTION_STATUSES.QC_COMPLETE,
      isInspection(repairRequest.status) ? 0 : inspectionTop.qcCount,
      showApiError,
    );

    if (isSuccess) {
      if (isInspection(repairRequest.status)) {
        navigate(INSPEX_PATHNAME.INSPECTION_INPUT_RESULT);
      } else {
        navigate(INSPEX_PATHNAME.REPORT, {
          state: { defaultQcCount: inspectionTop.qcCount },
        });
      }
    }
  };

  return (
    <Box p={0} sx={{ height: '100%', bgcolor: theme.palette.grey[100] }}>
      {inspectionTop && (
        <>
          {repairRequest && !isInspection(repairRequest.status) && (
            <TextField
              label={t('LBL_QC_COUNT')}
              size="small"
              disabled
              sx={{
                backgroundColor: 'background.default',
                borderRadius: '4px',
                margin: '1rem',
              }}
              slotProps={{
                input: {
                  sx: {
                    fontSize: '0.875rem',
                  },
                },
                inputLabel: {
                  sx: {
                    fontSize: theme.typography.caption.fontSize,
                  },
                },
              }}
              value={inspectionTop.qcCount}
            />
          )}
          <TextField
            label={t('LBL_INSPECTOR_NAME')}
            size="small"
            sx={{
              backgroundColor: 'background.default',
              borderRadius: '4px',
              margin: '1rem',
            }}
            slotProps={{
              input: {
                sx: {
                  fontSize: '0.875rem',
                },
              },
              inputLabel: {
                sx: {
                  fontSize: theme.typography.caption.fontSize,
                },
              },
              htmlInput: {
                maxLength: MaxLength.Inspection.INSPECTOR_NAME,
              },
            }}
            value={newInspector}
            onChange={handleInspectorChange}
            onBlur={handleInspectorBlur}
          />
          <Divider sx={{ width: '90%', margin: '0 auto 5px' }} />

          {inspectionTop.inspectionCategoryList.map((category, i) => {
            return (
              <Box
                key={`category_tab_${i}`}
                sx={{
                  cursor: 'pointer',
                  padding: '8px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  ...applyColorStyle(category.id, category.progress),
                }}
                onClick={() => onClickTab(category.id)}
              >
                <Typography
                  pl={1}
                  pr={1}
                  sx={[style.categoryTab, style.categoryName]}
                >
                  {category.label}
                </Typography>
                <Typography
                  sx={[
                    style.categoryTab,
                    { minWidth: '32px', textAlign: 'right' },
                  ]}
                >{`${category.progress}%`}</Typography>
              </Box>
            );
          })}
        </>
      )}
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{
          bgcolor: theme.palette.grey[100],
          pb: '1rem',
        }}
      >
        <Divider sx={{ width: '90%' }} />
        <Button
          variant="contained"
          color="error"
          sx={style.button}
          onClick={() => setIsOpenSaveProgress(true)}
        >
          {t('BTN_PAUSE')}
        </Button>
        {repairRequest && !isInspection(repairRequest.status) && (
          <Button
            variant="contained"
            color="inherit"
            sx={style.button}
            onClick={() => setIsOpenReturnRepair(true)}
          >
            {t('BTN_RETURN_REPAIR')}
          </Button>
        )}
        {isDisplayChangeButton && (
          <Button
            variant="contained"
            sx={{ ...style.button, bgcolor: 'warning.main' }}
            onClick={() => setIsOpenChangeInspection(true)}
          >
            {t('BTN_CHANGE_FULL_INSPECTION')}
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          sx={style.button}
          disabled={
            repairRequest && isInspection(repairRequest.status)
              ? !isAllComplete
              : !(isAllComplete && isAllOk)
          }
          onClick={() => onClickFinishTest()}
        >
          {repairRequest && t(getStringBtnFinishTest(repairRequest.status))}
        </Button>
        {/* 開発者向け：ALL OKボタン */}
        <Button
          variant="contained"
          sx={{ ...style.button, bgcolor: 'blue', display: 'none' }}
          onClick={onClickAllOk}
        >
          ALL OK
        </Button>
      </Box>
      <ConfirmModal
        isOpen={isOpenSaveProgress}
        displayMessage={{
          messageKey: 'MSG_PAUSE',
          values: { 0: t('LBL_INSPECTION') },
        }}
        onClickCancel={() => setIsOpenSaveProgress(false)}
        onClickOk={() => executeSaveProgress()}
      />
      <ConfirmModal
        isOpen={isOpenFinishTest}
        displayMessage={{
          messageKey: 'MSG_COMPLETE',
          values: { 0: t('LBL_INSPECTION') },
        }}
        onClickCancel={() => setIsOpenFinishTest(false)}
        onClickOk={() => executeFinishTest()}
      />
      <ConfirmModal
        isOpen={isOpenReturnRepair}
        displayMessage={t('MSG_RETURN_REPAIR')}
        onClickCancel={() => setIsOpenReturnRepair(false)}
        onClickOk={() => executeReturnRepair()}
      />
      <ConfirmModal
        isOpen={isOpenChangeInspection}
        displayMessage={t('MSG_CHANGE_INSPECTION')}
        onClickCancel={() => setIsOpenChangeInspection(false)}
        onClickOk={() => onClickOkChangeInspection()}
      />
      {error && <ErrorModal message={error} onClose={hideError} />}
      <Backdrop
        sx={{
          ...LOADING_BACKDROP_STYLE,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default CategoryTabs;
